/**
 * Dropping a pin on where a customer actually lives.
 *
 * WHY A MAP AND NOT A LOOKUP
 * --------------------------
 * Because a lookup does not work here. Handing a Pakistani address to a
 * geocoder returns something confident and wrong — tried on a real Lahore
 * address, it found the housing society and not the house. A pin in the wrong
 * street is worse than no pin, because the rider believes it, drives there,
 * and the food is cold before anybody rings.
 *
 * Somebody at the counter knows where the customer lives, or can ask them.
 * That is the only source worth trusting, and it takes one tap.
 *
 * WHY OPENSTREETMAP
 * -----------------
 * It is free, needs no key, no billing account and no per-request charge. A
 * Google map on the till would mean an account with a card attached and a bill
 * that grows with the number of orders, for a picture that does the same job.
 *
 * WHY IT ONLY HAS TO BE DONE ONCE
 * -------------------------------
 * The branch remembers the pin against the customer's telephone number, so a
 * regular is pinned the first time they order and never again. Without that
 * this would be a new job on every order, and on a Friday evening it would
 * quietly stop being done.
 */
import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Where the map opens when there is nothing to open on.
 *
 * Lahore city centre. A map that opens on the middle of the ocean makes the
 * cashier pan across a continent before they can start.
 */
const FALLBACK = { lat: 31.5204, lng: 74.3587 };

/**
 * Leaflet's default marker images are loaded by a path relative to the CSS,
 * which a bundler rewrites and then cannot find. Drawing the marker instead of
 * loading it avoids the whole problem and keeps the pin in the brand colour.
 */
const PIN = L.divIcon({
  className: '',
  html: `<div style="
    width:18px;height:18px;border-radius:9999px;
    background:#7ed957;border:3px solid #0d0d0d;
    box-shadow:0 0 0 2px #7ed957;
  "></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

/**
 * @param {object}   props
 * @param {number?}  props.latitude
 * @param {number?}  props.longitude
 * @param {Function} props.onChange   called with { latitude, longitude }
 */
export default function MapPin({ latitude, longitude, onChange }) {
  const holder = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);

  /**
   * The callback, kept in a ref so the map is not rebuilt every time the
   * parent re-renders with a fresh function.
   *
   * Written in an effect rather than during render. Assigning to a ref while
   * rendering is a rule React actually enforces, and the same slip in the
   * phone app's button was caught by lint there too.
   */
  const notify = useRef(onChange);

  useEffect(() => {
    notify.current = onChange;
  }, [onChange]);

  const pinned =
    Number.isFinite(Number(latitude)) &&
    Number.isFinite(Number(longitude)) &&
    !(Number(latitude) === 0 && Number(longitude) === 0);

  useEffect(() => {
    if (!holder.current || map.current) return;

    const start = pinned
      ? { lat: Number(latitude), lng: Number(longitude) }
      : FALLBACK;

    map.current = L.map(holder.current, {
      center: start,
      zoom: pinned ? 17 : 12,
      // A till is a touchscreen somebody is scrolling a menu on. A map that
      // grabs the wheel makes the page jump while they are looking elsewhere.
      scrollWheelZoom: false,
      attributionControl: true,
    });

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(map.current);

    if (pinned) {
      marker.current = L.marker(start, { icon: PIN, draggable: true }).addTo(map.current);
      marker.current.on('dragend', () => {
        const at = marker.current.getLatLng();
        notify.current?.({ latitude: at.lat, longitude: at.lng });
      });
    }

    map.current.on('click', (event) => {
      const at = event.latlng;

      if (marker.current) {
        marker.current.setLatLng(at);
      } else {
        marker.current = L.marker(at, { icon: PIN, draggable: true }).addTo(map.current);
        marker.current.on('dragend', () => {
          const moved = marker.current.getLatLng();
          notify.current?.({ latitude: moved.lat, longitude: moved.lng });
        });
      }

      notify.current?.({ latitude: at.lat, longitude: at.lng });
    });

    return () => {
      map.current?.remove();
      map.current = null;
      marker.current = null;
    };
    // Built once. The pin is moved through Leaflet rather than by rebuilding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * A pin arriving from elsewhere — the one the branch remembered for this
   * telephone number — moves the map to it.
   */
  useEffect(() => {
    if (!map.current || !pinned) return;

    const at = { lat: Number(latitude), lng: Number(longitude) };

    if (marker.current) {
      marker.current.setLatLng(at);
    } else {
      marker.current = L.marker(at, { icon: PIN, draggable: true }).addTo(map.current);
      marker.current.on('dragend', () => {
        const moved = marker.current.getLatLng();
        notify.current?.({ latitude: moved.lat, longitude: moved.lng });
      });
    }

    map.current.setView(at, 17);
  }, [latitude, longitude, pinned]);

  return (
    <div>
      <div
        ref={holder}
        role="application"
        aria-label="Map. Click to place the delivery pin."
        className="h-56 w-full overflow-hidden rounded-lg border border-rule"
      />
      <p className="mt-1 text-xs text-ink-3">
        {pinned
          ? 'Tap the map or drag the pin to move it.'
          : 'Tap the map where the customer lives.'}
      </p>
    </div>
  );
}
